import { useState } from "react";
import { Users, Settings, Gauge, Bike, ChevronLeft, ChevronRight, Flame, Crown } from "lucide-react";
import { BookingDialog } from "./BookingDialog";
import { useLang } from "../context/LanguageContext";

export function MotoCard({ moto }) {
  const { t } = useLang();
  const catLabel = t.cat[moto.category] || t.cat.motorcycle;
  const qty = moto.quantity ?? 1;
  const units = moto.available_units ?? (moto.available ? qty : 0);
  const images = moto.images && moto.images.length ? moto.images : [moto.image];
  const [idx, setIdx] = useState(0);
  const safeIdx = Math.min(idx, images.length - 1);
  const showUrgency = moto.available && units === 1;

  const prev = (e) => { e.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length); };
  const next = (e) => { e.stopPropagation(); setIdx((i) => (i + 1) % images.length); };

  return (
    <div
      data-testid={`moto-card-${moto.id}`}
      className="group bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col"
    >
      <div className="relative bg-gradient-to-br from-brand-sky/15 to-slate-50 p-2 flex items-center justify-center h-60 overflow-hidden">
        <img
          src={images[safeIdx]}
          alt={moto.name}
          data-testid={`moto-image-${moto.id}`}
          className="max-h-full max-w-full object-contain scale-125 group-hover:scale-150 transition-transform duration-500"
          loading="lazy"
        />

        {images.length > 1 && (
          <>
            <button onClick={prev} data-testid={`moto-img-prev-${moto.id}`} aria-label="Previous photo"
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white text-brand-gray rounded-full p-1.5 shadow opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronLeft size={18} />
            </button>
            <button onClick={next} data-testid={`moto-img-next-${moto.id}`} aria-label="Next photo"
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white text-brand-gray rounded-full p-1.5 shadow opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-3 right-3 z-10 flex gap-1">
              {images.map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all ${i === safeIdx ? "w-4 bg-brand-blue" : "w-1.5 bg-white/80"}`} />
              ))}
            </div>
          </>
        )}

        <span className="absolute top-3 left-3 bg-brand-blue text-white text-xs font-heading font-bold px-3 py-1 rounded-full">
          {moto.cc}cc
        </span>

        {moto.popular && (
          <span
            data-testid={`moto-popular-${moto.id}`}
            className="absolute top-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1 bg-gradient-to-r from-amber-400 to-brand-orange text-white text-[11px] font-heading font-black uppercase tracking-wide px-3 py-1 rounded-full shadow-md"
          >
            <Crown size={12} /> {t.common.mostPopular}
          </span>
        )}

        {showUrgency ? (
          <span
            data-testid={`moto-urgency-${moto.id}`}
            className="absolute bottom-3 left-3 inline-flex items-center gap-1 bg-brand-orange text-white text-xs font-heading font-black px-2.5 py-1 rounded-full shadow-md animate-pulse"
          >
            <Flame size={13} /> {t.common.onlyOneLeft}
          </span>
        ) : qty > 1 ? (
          <span
            data-testid={`moto-units-${moto.id}`}
            title={`${units} of ${qty} available`}
            className="absolute bottom-3 left-3 inline-flex items-center gap-1 bg-white/90 backdrop-blur text-brand-gray text-xs font-heading font-bold px-2.5 py-1 rounded-full shadow-sm"
          >
            <Bike size={13} className="text-brand-blue" /> {units}/{qty}
          </span>
        ) : null}

        <span
          data-testid={`moto-availability-${moto.id}`}
          className={`absolute top-3 right-3 text-xs font-heading font-bold px-3 py-1 rounded-full uppercase tracking-wide ${
            moto.available ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
          }`}
        >
          {moto.available ? t.common.available : t.common.rented}
        </span>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <span className="text-xs font-bold uppercase tracking-widest text-brand-sky">{catLabel}</span>
        <h3 className="font-heading font-extrabold text-lg text-brand-gray mt-1">{moto.name}</h3>

        <div className="flex flex-wrap gap-3 mt-3 text-sm text-slate-500">
          <span className="inline-flex items-center gap-1"><Users size={15} /> {moto.passengers} {t.common.pax}</span>
          <span className="inline-flex items-center gap-1"><Settings size={15} /> {moto.transmission}</span>
          <span className="inline-flex items-center gap-1"><Gauge size={15} /> {moto.engine}</span>
        </div>

        <div className="mt-auto pt-5">
          {moto.available ? (
            <BookingDialog
              motoName={moto.name}
              motoId={moto.id}
              trigger={
                <button
                  data-testid={`moto-book-${moto.id}`}
                  className="w-full bg-brand-orange hover:bg-[#d96b00] text-white font-heading font-extrabold text-base py-3.5 rounded-full transition-colors shadow-md hover:shadow-lg"
                >
                  {t.common.reserveNow}
                </button>
              }
            />
          ) : (
            <button
              disabled
              className="w-full bg-slate-200 text-slate-500 font-heading font-extrabold text-base py-3.5 rounded-full cursor-not-allowed"
            >
              {t.common.currentlyRented}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default MotoCard;
